import { format, isValid } from "date-fns";
import { useContext, useEffect, useState } from "react";

import AccessGroupList from "features/allocations/components/grid/access-group/AccessGroup";
import ContactCard from "shared/components/data-display/drawers/ContactCard";
import CustomErrors from "shared/constants/errorMessages";
import DataGroup from "shared/components/data-display/drawers/DataGroup";
import DrawerActions from "shared/components/data-display/drawers/DrawerActions";
import DrawerContext from "shared/contexts/DrawerContext";
import Loader from "shared/components/feedback/loading/Loader";
import PropTypes from "prop-types";
import Subject from "shared/components/data-display/drawers/Subject";
import VStack from "shared/components/layout/stack/VStack";
import env from "react-dotenv";
import { errorNotification } from "shared/components/feedback/notification/notification";
import usePageActionPermission from "shared/hooks/access/usePageActionPermission";
import { LoaderContainer } from "./EmployeeDrawerNew.styles";
import { useUser } from "shared/services/api/useUser";

const EmployeeDrawerNew = (props) => {
    const { userId } = props;
    const { getUserDetail } = useUser();
    const [profileLink, setProfileLink] = useState(null);
    const [user, setUser] = useState({
        id: null,
        name: null,
        title: null,
        photo: null,
        type: null,
        department: null,
        departmentId: null,
        location: null,
        talentManager: null,
        departureDate: null,
        community: null,
    });
    
    const canViewAllocations = usePageActionPermission(
        "AC_VIEW_OTHER_EMP_ALLOCATION",
        globalThis.config.routes.allocation
    );

    const { handleCloseMasterDrawer, setIsCTAPrimaryAction } = useContext(DrawerContext);

    useEffect(() => {
        if (!canViewAllocations) {
            setIsCTAPrimaryAction(false);
        }
    }, [canViewAllocations, setIsCTAPrimaryAction]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (userId) {
                const fetchedUser = await getUserDetail([{ name: "$userId", value: userId }]);

                setUser({
                    id: userId,
                    name: fetchedUser.FullName,
                    title: fetchedUser.Title,
                    photo:
                        fetchedUser.LargePhotoUrl &&
                        `${env.BESTWORK_REACT_ASSET_DOMAIN}/${fetchedUser.LargePhotoUrl}`,
                    type: fetchedUser.EmploymentType,
                    department: fetchedUser.Department,
                    departmentId: fetchedUser.DepartmentId,
                    seatOffice: fetchedUser.Office,
                    payOffice: fetchedUser.PayOffice,
                    talentManager: fetchedUser.TalentManager,
                    departureDate: fetchedUser.DepartureDate,
                    community: fetchedUser.Community,
                });

                setProfileLink(null);
            } else {
                errorNotification(undefined, CustomErrors.OnGetUser);
            }
        };

        fetchUserData();
    }, [userId, getUserDetail]);

    return (
        <>
            {userId && user.name ? (
                <>
                    <VStack spacing="Two">
                        <Subject
                            photoUrl={user.photo}
                            subjectName={user.name}
                            label={
                                user.type === "Freelancer" ? "FREELANCE" : null
                            }
                            user={user}
                            profileLink={profileLink}
                        />

                        <DataGroup
                            isPadding={false}
                            groupData={[
                                {
                                    label: "Title",
                                    value: user.title,
                                },
                                {
                                    label: "Department",
                                    value: user.department,
                                    isHr: true,
                                },
                                {
                                    label: "Pay Office",
                                    value: user.payOffice,
                                },
                                {
                                    label: " Community / Group",
                                    value: user.community ? (
                                        user.community
                                    ) : (
                                        <>&mdash;</>
                                    ),
                                    isHr: true,
                                },
                                {
                                    label: "Seat Office",
                                    value: user.seatOffice,
                                    isHr: !isValid(new Date(user.departureDate)),
                                },
                                {
                                    label: "Departure Date",
                                    value: isValid(new Date(user.departureDate))
                                        ? format(
                                            new Date(user.departureDate),
                                            "MMM dd, yyyy"
                                        )
                                        : null,
                                    isHr: true,
                                },
                            ]}
                        />

                        {user.talentManager && (
                            <ContactCard
                                photoUrl={
                                    env.BESTWORK_REACT_ASSET_DOMAIN +
                                    "/" +
                                    user.talentManager.SmallPhotoUrl
                                }
                                name={user.talentManager.FullName}
                                jobPost={user.talentManager.Title}
                                department={user.talentManager.Department}
                                emailId={user.talentManager.Email}
                            />
                        )}

                        <AccessGroupList
                            userId={userId}
                            departmentId={user.departmentId}
                            onClose={handleCloseMasterDrawer}
                        />
                    </VStack>
                    <DrawerActions
                        primaryAction={
                            canViewAllocations
                                ? {
                                    isInternalUrlNewTab: true,
                                    label: "View Employee Allocations",
                                    href: `${globalThis.config.routes.allocation}?userId=${userId}`,
                                }
                                : null
                        }
                        tertiaryAction={{
                            "aria-label":
                                user.type === "Freelancer"
                                    ? "Skill Search not available for Freelance"
                                    : "",
                            type: "button",
                            label: "Skill Search Profile",
                            to: profileLink,
                            disabled: !profileLink,
                        }}
                    />
                </>
            ) : (
                <LoaderContainer>
                    <Loader height="5rem" width="5rem" />
                </LoaderContainer>
            )}
        </>
    );
};

EmployeeDrawerNew.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default EmployeeDrawerNew;
